import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";
import { toast } from "react-toastify";
import UserTypes from "components/auth/user-types";
import Spinner from "components/common/Spinner";
import exportFromBlob from "helpers/export-from-blob";
import {
    closeCustomModal,
    openCustomModal
} from "components/custom/modals";
import {
    fetchCustomerInvoicingCharges,
    fetchMerchandiserPdfInvoice,
    approveMerchandiserInvoices,
    customerGenerateInvoice,
    customerExportChargesExcel
} from "api/menu/invoicing/actions";

import ListChargesHeader from "components/menu/invoicing/customer/list-charges-header";
import ListChargesCharges from "components/menu/invoicing/customer/list-charges-charges";
import MerchandiserInvoicingStatuses from "data/merchandiser-invoicing-statuses";

const ServiceReport = React.lazy(() =>
    import("components/menu/reporting/service-report"));
const PdfViewerModal = React.lazy(() =>
    import("components/menu/invoicing/customer/pdf-viewer-modal"));
const ViewChargeModal = React.lazy(() =>
    import("components/menu/invoicing/customer/view-charge-modal"));

class ListCharges extends React.Component {
    dateFormat = "YYYY-MM-DD";

    defaultFilters = {
        fromDate: moment().startOf("month"),
        toDate: moment().endOf("month"),
        jobId: null,
        supplerId: null,
        hideMatchingReversals: false
    };

    constructor(props) {
        super(props);

        this.state = {
            filters: {
                fromDate: moment().startOf("month"),
                toDate: moment().endOf("month"),
                jobId: null,
                supplerId: null,
                hideMatchingReversals: false
            },

            selectedCharges: {},
        };

        this.getChargesFilter = this.getChargesFilter.bind(this);
        this.getRequestData = this.getRequestData.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handleApprove = this.handleApprove.bind(this);
        this.handleLoad = this.handleLoad.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.handleGeneratePreliminaryInvoice = this.handleGeneratePreliminaryInvoice.bind(this);
        this.handlePrintCharges = this.handlePrintCharges.bind(this);
        this.viewReport = this.viewReport.bind(this);
        this.handleViewPreliminaryInvoice = this.handleViewPreliminaryInvoice.bind(this);
        this.handleToggleSelectedCharges = this.handleToggleSelectedCharges.bind(this);

        this.handleView = this.handleView.bind(this);
    }

    componentDidMount() {
        this.handleLoad();
    }

    handleToggleSelectedCharges = (invoiceSummaryId = false, ref = null) => {
        let selectedCharges = Object.assign({}, this.state.selectedCharges);
        if (invoiceSummaryId) {
            if (selectedCharges[invoiceSummaryId]) {
                delete selectedCharges[invoiceSummaryId];
            } else {
                selectedCharges[invoiceSummaryId] = true;
            }
        } else if (Object.keys(selectedCharges).length) {
            selectedCharges = {};
        } else {
            const { charges } = this.props;
            if (ref) {
                const data = ref.current.getResolvedState().sortedData;
                data.forEach(item => {
                    if (item.payApprovalStatus !== MerchandiserInvoicingStatuses.NotApprovedYet.key
                        && item.payApprovalStatus !==
                        MerchandiserInvoicingStatuses.RejectedByManager.key) {
                        selectedCharges[item._original.invoiceSummaryId] = true;
                    }
                });
            } else {
                (charges || []).forEach(item => {
                    if (item.payApprovalStatus !== MerchandiserInvoicingStatuses.NotApprovedYet.key
                        && item.payApprovalStatus !==
                            MerchandiserInvoicingStatuses.RejectedByManager.key) {
                        selectedCharges[item.invoiceSummaryId] = true;
                    }
                });
            }
        }

        this.setState({
            selectedCharges
        });
    };

    getRequestData = () => {
        const {
            fromDate,
            toDate,
            jobId,
            supplierId,
        } = this.state.filters;

        const requestData = {};
        if (fromDate) {
            requestData.fromDate = moment(fromDate).format(this.dateFormat);
        }
        if (toDate) {
            requestData.toDate = moment(toDate).format(this.dateFormat);
        }
        if (jobId) {
            requestData.jobId = jobId.value;
        }
        if (supplierId) {
            requestData.supplierId = supplierId.value;
        }

        return requestData;
    };

    handleSelect = (inputsArr) => {
        const filters = Object.assign({}, this.state.filters);
        inputsArr.forEach(item => {
            filters[item.name] = item.value;
        });
        this.setState({
            filters
        });
    };

    handleApprove = () => {
        const { selectedCharges } = this.state;
        const data = Object.keys(selectedCharges)
            .map(id => parseInt(id, 10));
        if (!data.length) {
            toast("Please select one or more charges.", {
                type: toast.TYPE.WARNING,
            });
            return false;
        }
        this.props.approveMerchandiserInvoices(data, () => {
            this.setState({
                selectedCharges: {},
            }, this.handleLoad);
        });
    };

    handleLoad = ()  => {
        this.setState({
            selectedCharges: {},
        }, () => this.props.fetchCustomerInvoicingCharges(this.getRequestData()));
    };

    handleReset = () => {
        this.setState({
            filters: this.defaultFilters,
            selectedCharges: {},
        }, () => this.props.fetchCustomerInvoicingCharges(this.getRequestData()));
    };

    handleGeneratePreliminaryInvoice = () => {
        const { selectedCharges } = this.state;
        const data = Object.keys(selectedCharges)
            .map(id => parseInt(id, 10));

        if (!data.length) {
            toast("Please select one or more charges.", {
                type: toast.TYPE.WARNING,
            });
            return false;
        }

        this.props.customerGenerateInvoice(data, () => {
            this.setState({
                selectedCharges: {},
            });
        });
    };

    handlePrintCharges = () => {
        const { selectedCharges } = this.state;
        const data = Object.keys(selectedCharges)
             .map(id => parseInt(id, 10));

        if (!data.length) {
            toast("Please select one or more charges.", {
                type: toast.TYPE.WARNING,
            });
            return false;
        }
        this.props.customerExportChargesExcel(data, response => {
            const fileName =  response.headers["content-disposition"].split("''")[1] ||
                `CustomerCharges ${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`;
            exportFromBlob(response.data, fileName);
        });
    };

    viewReport = (serviceId) => {
        const modalObj = {
            id: "view-report",
            modalSettings: {
                size: "lg",
                style: {
                    top: 20,
                    width: "calc(100% - 40px)"
                },
            },
            header: `Service Report: ${serviceId}`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ServiceReport
                        serviceId={serviceId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSave={() => {
                            this.props.closeCustomModal(modalObj);
                        }}
                        onSubmit={() => {
                            this.props.closeCustomModal(modalObj);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleView = (chargeId) => {
        const modalObj = {
            id: "view-misc-charge",
            modalSettings: {
                size: "sm",
            },
            header: `View Charge Detail ID: ${chargeId}`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ViewChargeModal
                        chargeId={chargeId}
                        onViewReport={this.viewReport}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSave={() => {
                            this.handleLoad();
                            this.props.closeCustomModal(modalObj);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleViewPreliminaryInvoice = () => {
        this.props.fetchMerchandiserPdfInvoice(this.getRequestData(), (pdfData) => {
            const modalObj = {
                id: "view-preliminary-invoice",
                modalSettings: {
                    size: "lg",
                    style: {
                        top: 20,
                        width: "calc(100% - 40px)"
                    },
                },
                header: "View Preliminary Invoice",
                children: (
                    <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                        <PdfViewerModal
                            pdfData={pdfData}
                            name="preliminary-invoice.pdf"
                        />
                    </React.Suspense>
                ),
                handleClose: () => this.props.closeCustomModal(modalObj)
            };
            this.props.openCustomModal(modalObj);
        });
    };

    getChargesFilter = () => {
        const charges = Object.assign([], this.props.charges);
        const newCharges = [];
        if (this.state.filters.hideMatchingReversals) {
            charges.forEach(item => {
                const reverseIndex = charges.findIndex(reversesItem =>
                    reversesItem.jobId === item.jobId
                    && reversesItem.storeId === item.storeId
                    && reversesItem.completedDate === item.completedDate
                    && reversesItem.amount === -1 * item.amount
                    && reversesItem.amount !== 0);
                if (reverseIndex === -1) {
                    newCharges.push(item);
                }
            });
            return newCharges;
        }
        return charges;
    };

    render() {
        const {
            loading,
            userRole
        } = this.props;
        const { selectedCharges } = this.state;

        const charges = this.getChargesFilter();

        const isAdminView = userRole.includes(UserTypes.admin);

        return (
            <div className="position-relative">
                {!loading || <Spinner />}

                <ListChargesHeader
                    handleLoad={this.handleLoad}
                    handleReset={this.handleReset}
                    handleGeneratePreliminaryInvoice={this.handleGeneratePreliminaryInvoice}
                    selectedChargesCount={Object.keys(selectedCharges).length}
                    handlePrintCharges={this.handlePrintCharges}
                    handleViewPreliminaryInvoice={this.handleViewPreliminaryInvoice}
                    handleSelect={this.handleSelect}
                    handleApprove={this.handleApprove}
                    filters={this.state.filters}
                />

                <div className="mb-4">
                    <ListChargesCharges
                        charges={charges || []}
                        onToggle={this.handleToggleSelectedCharges}
                        isAdminView={isAdminView}
                        selectedCharges={this.state.selectedCharges}
                        onViewReport={this.viewReport}
                        onView={this.handleView}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    userRole: state.auth.user.role,
    loading: state.invoicing.loading,
    charges: state.invoicing.customerCharges
});

const mapStateToDispatch = dispatch => ({
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

    fetchCustomerInvoicingCharges:
        bindActionCreators(fetchCustomerInvoicingCharges, dispatch),
    fetchMerchandiserPdfInvoice: bindActionCreators(fetchMerchandiserPdfInvoice, dispatch),
    approveMerchandiserInvoices: bindActionCreators(approveMerchandiserInvoices, dispatch),
    customerGenerateInvoice: bindActionCreators(customerGenerateInvoice, dispatch),
    customerExportChargesExcel: bindActionCreators(customerExportChargesExcel, dispatch),
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ListCharges);
